import React, { useState, useRef, useEffect } from 'react';
import { sendMessage } from '../ai/aiService';
import { EMERGENCY_KEYWORDS } from '../ai/systemPrompt';

const STARTER_PROMPTS = [
  'What preventative screenings am I due for?',
  'What does my insurance cover?',
  'Help me find a specialist near me',
  'Do I need a referral to see a dermatologist?',
];

function isEmergency(text) {
  const lower = text.toLowerCase();
  return EMERGENCY_KEYWORDS.some(kw => lower.includes(kw));
}

// Render basic markdown: **bold** and *italic* as styled spans, newlines as <br>
function renderMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part.split('\n').map((line, j, arr) => (
      <span key={`${i}-${j}`}>{line}{j < arr.length - 1 ? <br /> : null}</span>
    ));
  });
}

export default function AIAssistant({ placeholder = 'Ask your health assistant…', starterPrompts }) {
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const threadRef   = useRef(null);
  const lastMsgRef  = useRef(null);
  const inputRef    = useRef(null);

  // Auto-resize textarea to fit content
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [input]);

  useEffect(() => {
    if (!threadRef.current || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === 'user' || loading) {
      // Show loading dots after user sends
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    } else {
      // Scroll to the top of the assistant's reply so user reads from the beginning
      lastMsgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages, loading]);

  async function handleSend(text) {
    const content = (text || input).trim();
    if (!content || loading) return;

    setInput('');
    setError(null);

    if (isEmergency(content)) {
      setMessages(prev => [
        ...prev,
        { role: 'user', content },
        {
          role: 'assistant',
          content: '🚨 This sounds like it could be a medical emergency. Please call 911 or go to your nearest ER immediately. Do not wait.',
          isEmergency: true,
        },
      ]);
      return;
    }

    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setLoading(true);

    try {
      const reply = await sendMessage(nextMessages);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const activeStarters = starterPrompts || STARTER_PROMPTS;
  const showStarters = messages.length === 0 && !loading;

  return (
    <div style={s.wrap}>
      {/* Non-dismissible disclaimer */}
      <div style={s.disclaimer}>
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.2"/>
          <path d="M6.5 4.5v3M6.5 9v.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        </svg>
        AI assistant · Not medical advice · Always consult a licensed provider
      </div>

      {/* Starter prompts */}
      {showStarters && (
        <div style={s.starters}>
          {activeStarters.map(p => (
            <button key={p} style={s.starterChip} onClick={() => handleSend(p)}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Message thread */}
      {messages.length > 0 && (
        <div style={s.thread} ref={threadRef}>
          {messages.map((msg, i) => (
            <div
              key={i}
              ref={i === messages.length - 1 ? lastMsgRef : null}
              style={{
                ...s.bubble,
                ...(msg.role === 'user' ? s.bubbleUser : s.bubbleAssistant),
                ...(msg.isEmergency ? s.bubbleEmergency : {}),
              }}
            >
              {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
            </div>
          ))}
          {loading && (
            <div style={{ ...s.bubble, ...s.bubbleAssistant, ...s.bubbleLoading }}>
              <span style={s.dot} />
              <span style={{ ...s.dot, animationDelay: '0.15s' }} />
              <span style={{ ...s.dot, animationDelay: '0.3s' }} />
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && <div style={s.errorText}>{error}</div>}

      {/* Input */}
      <div style={s.inputRow}>
        <textarea
          ref={inputRef}
          style={s.input}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={loading}
        />
        <button
          style={{ ...s.sendBtn, opacity: (!input.trim() || loading) ? 0.4 : 1 }}
          onClick={() => handleSend()}
          disabled={!input.trim() || loading}
          aria-label="Send"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M14 8L2 2l2.5 6L2 14l12-6z" fill="currentColor"/>
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes cp-bounce {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

const s = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    background: 'var(--surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  disclaimer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    background: 'var(--teal-faint)',
    borderBottom: '1px solid rgba(30,92,84,0.12)',
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--teal)',
    letterSpacing: '0.02em',
  },
  starters: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px 16px 4px',
  },
  starterChip: {
    textAlign: 'left',
    padding: '10px 14px',
    background: 'var(--cream)',
    border: '1.5px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)',
    fontSize: '13px',
    color: 'var(--ink)',
    cursor: 'pointer',
    lineHeight: 1.4,
  },
  thread: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxHeight: '320px',
    overflowY: 'auto',
    padding: '4px 16px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  bubble: {
    maxWidth: '85%',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
    lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  bubbleUser: {
    alignSelf: 'flex-end',
    background: 'var(--teal)',
    color: 'white',
    borderBottomRightRadius: '4px',
  },
  bubbleAssistant: {
    alignSelf: 'flex-start',
    background: 'var(--cream)',
    color: 'var(--ink)',
    border: '1px solid var(--border)',
    borderBottomLeftRadius: '4px',
  },
  bubbleEmergency: {
    background: '#fef3f0',
    border: '1px solid rgba(192,57,43,0.25)',
    color: '#c0392b',
  },
  bubbleLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '12px 16px',
  },
  dot: {
    display: 'inline-block',
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: 'var(--ink-4)',
    animation: 'cp-bounce 1.2s ease-in-out infinite',
  },
  errorText: {
    padding: '0 16px',
    fontSize: '12px',
    color: '#c0392b',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid var(--border)',
  },
  input: {
    flex: 1,
    resize: 'none',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1.5px solid var(--border-strong)',
    background: 'var(--cream)',
    fontFamily: 'var(--font-body)',
    fontSize: '14px',
    color: 'var(--ink)',
    outline: 'none',
    lineHeight: 1.5,
    maxHeight: '120px',
    overflowY: 'auto',
  },
  sendBtn: {
    width: '40px',
    height: '40px',
    borderRadius: 'var(--radius-md)',
    background: 'var(--teal)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'opacity 0.15s',
  },
};
